<template>
  <div
    class="modal"
    :style="{
      '--backgroundImageSmall': `url(${backgroundImages?.smallMedia})`,
      '--backgroundImageMedium': `url(${backgroundImages?.mediumMedia})`,
      '--backgroundImageLarge': `url(${backgroundImages?.largeMedia})`,
    }"
  >
    <a href="/" class="logo">
      <img src="@/assets/images/logo.svg" class="header__logo" />
    </a>
    <div class="modal__content">
      <div class="modal__video-background">
        <Media
          :images="images"
          :sources="leftMediaSources"
          cover
          class="media"
          cover-position="top center"
        />
      </div>
      <div class="modal__content-inner">
        <LandingOpening
          v-if="showLandingOpening"
          :end-date="endDate"
          @login="shownAccountForm = 'login'"
          @register="shownAccountForm = 'register'"
        />
        <LandingSuccess v-else-if="showLandingSuccess" :end-date="endDate" />
        <template v-else-if="shownAccountForm">
          <LandingRegisterForm
            v-if="shownAccountForm === 'register'"
            :end-date="endDate"
            :return-url="landingReturnUrl"
            @success="handleSuccess"
            @login="shownAccountForm = 'login'"
          />
          <LandingLoginForm
            v-if="shownAccountForm === 'login'"
            :return-url="landingReturnUrl"
            @success="handleSuccess"
            @register="shownAccountForm = 'register'"
          />
        </template>
      </div>
    </div>
    <div class="modal__images">
      <div class="video-container">
        <Media
          :images="images"
          :sources="videoSources"
          cover
          class="media"
          cover-position="top center"
        />
      </div>
      <div class="overlay"></div>
    </div>
  </div>
</template>

<script>
import Media from "@/components/Media.vue";
import LandingRegisterForm from "@/components/landing/LandingRegisterForm.vue";
import LandingLoginForm from "@/components/landing/LandingLoginForm.vue";
import LandingOpening from "@/components/landing/LandingOpening.vue";
import LandingSuccess from "@/components/landing/LandingSuccess.vue";
import { mapGetters } from "vuex";

export default {
  name: "Landing",
  components: {
    Media,
    LandingRegisterForm,
    LandingLoginForm,
    LandingOpening,
    LandingSuccess,
  },
  inject: ["mq"],
  data() {
    return {
      success: false,
      shownAccountForm: null,
      // Video sources for the background Media component.
      // Leave this as an empty array to fallback to images-only.
      videoSources: [
        {
          url: "https://cdn.shopify.com/videos/c/o/v/4e297bdfe8d740789b49e599b5a89c81.mp4",
          mimeType: "video/mp4",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapGetters("app", ["currencyCode"]),
    endDate() {
      // 27 Sep 2026 — local sale-start times → UTC
      const dates = {
        gbp: "2026-09-27T17:00:00Z", // 18:00 BST
        eur: "2026-09-27T17:00:00Z", // 19:00 CEST (7pm CET as listed)
        usd: "2026-09-27T17:00:00Z", // 13:00 EDT
        aud: "2026-09-27T03:00:00Z", // 13:00 AEST
      };
      return dates[this.currencyCode] || dates.gbp;
    },
    showLandingOpening() {
      if (this.isLoggedIn || this.success) {
        return false;
      }

      return this.shownAccountForm === null;
    },
    showLandingSuccess() {
      return this.success || this.isLoggedIn;
    },
    landingReturnUrl() {
      if (typeof window === "undefined") {
        return "/pages/landing";
      }

      return window.location.pathname;
    },
    // Mobile: still image until success; video only after login/signup.
    // Desktop: this block is hidden (video plays in the right column).
    leftMediaSources() {
      return this.showLandingSuccess ? this.videoSources : [];
    },
    images() {
      return {
        smallMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_d1bdd8c1-3e39-47e7-b1a4-1b3837b4d576.jpg?v=1789641642",
        mediumMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_d1bdd8c1-3e39-47e7-b1a4-1b3837b4d576.jpg?v=1789641642",
        largeMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_4_895111a9-787f-4b4b-b0fc-1979e1c31c71.jpg?v=1788354132",
        largeMedia2x:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_4_895111a9-787f-4b4b-b0fc-1979e1c31c71.jpg?v=1788354132",
      };
    },
    backgroundImages() {
      return {
        smallMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_d1bdd8c1-3e39-47e7-b1a4-1b3837b4d576.jpg?v=1789641642",
        mediumMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_d1bdd8c1-3e39-47e7-b1a4-1b3837b4d576.jpg?v=1789641642",
        largeMedia:
          "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_4-2.jpg?v=1789641642",
      };
    },
  },
  methods: {
    handleSuccess() {
      this.shownAccountForm = null;
      this.success = true;
    },
  },
};
</script>

<style lang="scss">
#shopify-section-header,
#shopify-section-footer {
  display: none;
}
</style>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";

.modal {
  position: fixed;
  z-index: 1;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;

  @include respond-above(md) {
    display: flex;
    height: 100vh;
    overflow: hidden;
  }

  &__content,
  &__images {
    flex: 0 0 50%;
    position: relative;
    overflow: hidden;
  }

  .close-btn {
    position: absolute;
    padding: 10px;
    border-radius: 50%;
    top: 8px;
    right: 8px;
    z-index: 1;

    @include respond-above(md) {
      top: 20px;
      right: 20px;
    }
  }
}

.logo {
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  filter: invert(1);

  @include respond-above(md) {
    display: none;
  }
}

.modal__content {
  color: $color-white;
  height: 100vh;
  position: relative;
  overflow-y: auto;
  padding-top: 80px;
  background-image: none;

  @include respond-above(md) {
    padding-top: 40px;
    background-image: var(--backgroundImageLarge);
    background-size: cover;
    background-position: top center;
    background-repeat: no-repeat;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      z-index: 1;
      pointer-events: none;
      /* Darken photo so centred copy matches mobile legibility */
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.35) 0%,
        rgba(0, 0, 0, 0.55) 42%,
        rgba(0, 0, 0, 0.72) 100%
      );
    }
  }

  .modal__video-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    overflow: hidden;

    .media {
      height: 100%;
      width: 100%;
    }

    @include respond-above(md) {
      display: none;
    }
  }
}

.modal__content-inner {
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
  position: absolute;
  z-index: 2;
  text-align: center;
  @extend .containerV2;
  padding: 20px;
  max-width: 480px;
  width: 100%;
  margin: 0 auto;

  @include respond-above(md) {
    padding: 40px;
  }
}

.modal__images {
  display: none;

  @include respond-above(md) {
    display: block;
    position: relative;
    height: 100vh;
    width: 50%;
    overflow: hidden;
    background-color: #000;
  }

  .video-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .media {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: transparent;
    z-index: 5;
  }
}

.media {
  height: 100%;
  width: 100%;

  &:deep(video) {
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: center;
  }
}
</style>
